import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'assets/styles/pages/task-clearance-plan.scss';
import { Accordion, AccordionItem } from 'react-accessible-accordion';
import { TCPNavigationStatuses } from 'consts/TCP';
import { useValidationComments } from 'hooks/useValidationComments';
import { Loader } from 'component/Loader';
import { REPORT_TYPES } from 'consts/ReportValidation';
import { useDailyFormManagement } from 'hooks/useDailyFormManagement';
import { NavigationTabLinks } from '../../Tabs/NavigationLinks';
import { DMSBlockView } from '../Blocks/DMSBlock/DMSBlock';
import { FieldWorkBlockView } from '../Blocks/FieldWorkBlock/FieldWorkBlock';
import { TranslateWrapperComponent } from 'component/Translation';
import { ReactComponent as TranslateSvg } from 'assets/images/icons/translate_24px.svg';

export const DailyViewPage = () => {
  const { clearanceId, dailyId } = useParams();
  const [navigationStatuses, setNavigationStatuses] = useState(TCPNavigationStatuses);
  const validationComments = useValidationComments(REPORT_TYPES.DAILY, dailyId);
  const [translateState, setTranslateState] = useState(false);
  const [translatedFormData, setTranslatedFormData] = useState({});

  const {
    formData,
    loader,
    gridGeojson,
    haReport,
    mapLoader,
    coords3857,
    zone,
    setGridGeojson,
    dispatch,
    crosshair,
    eoFoundChoices
  } = useDailyFormManagement(clearanceId, dailyId);

  const createData = useMemo(() => {

    if (!formData?.info) {
      return { type: 'Empty' };
    }

    if (clearanceId === 'empty') {
      return { type: 'Empty' };
    }

    if (formData?.info?.ha_report_id && !formData?.info?.clearance_task_id) {
      return { type: 'HA' };
    }

    if (formData?.info?.clearance_task_id && !formData?.info?.ha_report_id) {
      return { type: 'TCP' };
    }

    return { type: 'Empty' };
  }, [clearanceId, formData?.info?.ha_report_id, formData?.info?.clearance_task_id]);

  useEffect(() => {
    setNavigationStatuses((prev) => ({
      ...prev,
      tcpCreate: 'completed',
      daily: 'active',
      weekly: 'inactive',
      completed: 'inactive',
      done: 'inactive',
    }));
    document.title = 'View daily';
  }, []);

  return (
    <section>
      <div className="flex-column-group">
        {false && (
          <NavigationTabLinks
            statuses={navigationStatuses}
            mode="view"
            id={clearanceId}
          />
        )}
        <TranslateWrapperComponent
          formData={{ ...formData, report_type: 'daily_report' }}
          setTranslatedFormData={setTranslatedFormData}
          translationState={translateState}
        >
          <div className="text-hover flex-group flex-right" onClick={() => setTranslateState(!translateState)}>
            <TranslateSvg />
            <span>Translate</span>
          </div>
          <DailyViewForm
            formData={formData}
            dispatch={dispatch}
            coords3857={coords3857}
            haReportPolygon={haReport?.info?.turn_points?.geojson}
            zone={zone}
            setGridGeojson={setGridGeojson}
            gridGeojson={gridGeojson}
            mapLoader={mapLoader}
            commentsData={validationComments}
            translatedFormData={translatedFormData}
            translateState={translateState}
            crosshair={crosshair}
            eoFoundChoices={eoFoundChoices}
            loader={loader}
          />
        </TranslateWrapperComponent>
      </div>
      <Loader visible={loader} />
      <ToastContainer />
    </section>
  );
};

const DailyViewForm = ({
  formData,
  dispatch,
  coords3857,
  zone,
  gridGeojson,
  haReportPolygon,
  mapLoader,
  commentsData,
  setGridGeojson,
  translatedFormData = {},
  translateState = false,
  crosshair,
  eoFoundChoices,
  loader,
}) => {
  const isEmptyType = formData?.info?.clearance_task_id === null && formData?.info?.ha_report_id === null;
  const isHAType = formData?.info?.ha_report_id !== null && formData?.info?.clearance_task_id === null;
  const isTCPType = formData?.info?.clearance_task_id !== null && formData?.info?.ha_report_id === null;

  return (
  <div className="daily-form">
    <div className="form-container flex-column-group">
      <DailyFormHeader />
      <Accordion
        allowMultipleExpanded
        allowZeroExpanded
        preExpanded={[
          'dms', 'fieldWork', 'marking', 'battle-area-clearance', 'eoFound']}
        className="custom-accordion"
      >
        <AccordionItem uuid="dms">
          <DMSBlockView
            formData={formData}
            dispatch={dispatch}
            commentsData={commentsData}
            mode="view"
            translatedFormData={translatedFormData}
            translateState={translateState}
            isEmptyType={isEmptyType}
            isHAType={isHAType}
          />
        </AccordionItem>
        {formData?.info?.dms?.team_activity === 'field_work' && (
          <AccordionItem uuid="fieldWork">
            <FieldWorkBlockView
              formData={formData}
              dispatch={dispatch}
              setGridGeojson={setGridGeojson}
              haReportPolygon={haReportPolygon}
              coords3857={coords3857}
              zone={zone}
              gridGeojson={gridGeojson}
              loader={mapLoader}
              mode="view"
              commentsData={commentsData}
              translatedFormData={translatedFormData}
              translateState={translateState}
              crosshair={crosshair}
            />
          </AccordionItem>
        )}
      </Accordion>
    </div>
  </div>
  );
};

const DailyFormHeader = () => (
  <div className="form-header">
    <span>View a daily</span>
    <div className="line" />
  </div>
);
